import { useOutletContext } from "react-router";
import { format } from "date-fns";
import { ArrowLeft, Star, Trash, Reply, Forward, Archive } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Avatar } from "@/components/ui/avatar";
import { type Email } from "@/types";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";

type EmailContextType = {
  emails: Email[];
  selectedEmailId: string | null;
  setSelectedEmailId: (id: string | null) => void;
};

export const EmailHome = () => {
  const { emails, selectedEmailId, setSelectedEmailId } = useOutletContext<EmailContextType>();

  const selectedEmail = emails && emails.find((email) => email.id === selectedEmailId);

  if (!selectedEmail) {
    return (
      <div className="flex h-full items-center justify-center px-6">
        <div className="max-w-sm text-center">
          <h3 className="text-lg font-medium">No email selected</h3>
          <p className="text-muted-foreground">Select an email from the sidebar to view it here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      <div className="flex flex-col gap-3 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSelectedEmailId(null)}
            className="shrink-0 md:hidden"
            aria-label="Back to email list"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="min-w-0 truncate text-lg font-semibold sm:text-xl">{selectedEmail.subject}</h2>
          {!selectedEmail.parsed && (
            <Badge variant="outline" className="shrink-0">
              New
            </Badge>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-1 overflow-x-auto">
          <Button variant="ghost" size="icon" aria-label="Star email">
            <Star
              className="h-4 w-4"
              fill={selectedEmail.parsed ? "currentColor" : "none"}
            />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Reply">
            <Reply className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Forward">
            <Forward className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Archive">
            <Archive className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" aria-label="Delete">
            <Trash className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <ScrollArea className="flex-1">
        <div className="w-full">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3 p-4">
              <Avatar className="h-10 w-10 shrink-0">
                <div className="bg-primary text-primary-foreground flex h-full w-full items-center justify-center rounded-full text-sm font-medium uppercase">
                  {selectedEmail.participants[0].address.charAt(0)}
                </div>
              </Avatar>
              <div className="min-w-0">
                <div className="truncate font-medium">{selectedEmail.participants[0].address}</div>
                <div className="truncate text-sm text-muted-foreground">
                  To: {selectedEmail.participants[1].address}
                </div>
              </div>
            </div>
            <div className="px-4 pb-4 text-sm text-muted-foreground sm:p-4 sm:text-right">
              {format(selectedEmail.createdAt, "MMM d, yyyy 'at' h:mm a")}
            </div>
          </div>
          <Separator />
          <div className="w-full whitespace-pre-wrap break-words p-4">{selectedEmail.bodyText}</div>
        </div>
      </ScrollArea>
    </div>
  );
};

export default EmailHome;
